import {
  Link,
  createFileRoute,
  redirect,
  useRouter,
} from '@tanstack/react-router'
import {
  Button,
  Card,
  Content as CardContent,
  Header as CardHeader,
  Heading as CardTitle,
} from '@react-spectrum/s2'
import { Download, MessageSquare, RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  getPlanCheck,
  restartPlanCheck,
  retryPlanCheck,
} from '@rpc/plan-check.rpc'
import { trackAnalyticsEvent } from '@client/analytics'
import { fullWidthStyles } from '@/styles/s2-macros'

const PLAN_CHECK_STALE_RETRY_SECONDS = 330

export const Route = createFileRoute('/plan-checks/$runId')({
  loader: async ({ params }) => {
    try {
      return await getPlanCheck({ data: { runId: params.runId } })
    } catch {
      throw redirect({ to: '/plan-checks' })
    }
  },
  component: PlanCheckDetailPage,
  head: () => ({
    meta: [{ title: 'Plan check report - BuildingRegs.app' }],
  }),
})

function statValue(value: unknown) {
  return typeof value === 'number' ? value : 0
}

function resultTone(result: string) {
  if (result === 'issue') return 'border-red-200 bg-red-50 text-red-800'
  if (result === 'uncertain_needs_verification')
    return 'border-amber-200 bg-amber-50 text-amber-800'
  if (result === 'pass') return 'border-green-200 bg-green-50 text-green-800'
  return 'border-neutral-200 bg-neutral-50 text-neutral-700'
}

function statusTone(status: string) {
  if (status === 'failed') return 'border-red-200 bg-red-50 text-red-800'
  if (status === 'completed_with_warnings') {
    return 'border-amber-200 bg-amber-50 text-amber-800'
  }
  if (status === 'completed')
    return 'border-green-200 bg-green-50 text-green-800'
  return 'border-blue-200 bg-blue-50 text-blue-800'
}

function stepTone(state: string) {
  if (state === 'failed') return 'border-red-500'
  if (state === 'running') return 'border-blue-500'
  if (state === 'warning') return 'border-amber-500'
  if (state === 'completed') return 'border-green-500'
  return 'border-neutral-300'
}

function formatSeconds(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '0s'
  if (value < 60) return `${value}s`
  const minutes = Math.floor(value / 60)
  const seconds = value % 60
  if (minutes < 60) return `${minutes}m ${seconds}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

type AssessmentLike = {
  id: string
  result: string
  part?: string | null
  title: string
  explanation: string
}

export function filterPlanCheckAssessments<T extends AssessmentLike>(
  assessments: Array<T>,
  filters: { result: string; part: string; query: string },
) {
  const query = filters.query.trim().toLowerCase()
  return assessments.filter((assessment) => {
    if (filters.result && assessment.result !== filters.result) return false
    if (filters.part && (assessment.part ?? '') !== filters.part) return false
    if (!query) return true
    return `${assessment.title} ${assessment.explanation}`
      .toLowerCase()
      .includes(query)
  })
}

export function groupPlanCheckAssessments<T extends AssessmentLike>(
  assessments: Array<T>,
) {
  return {
    issues: assessments.filter((item) => item.result === 'issue'),
    uncertain: assessments.filter(
      (item) => item.result === 'uncertain_needs_verification',
    ),
    notAssessable: assessments.filter(
      (item) => item.result === 'not_assessable',
    ),
  }
}

export function buildPlanCheckTimeline<
  T extends { id: string; stage: string; message: string },
>(events: Array<T>) {
  return events.map((event) => ({
    id: event.id,
    stage: event.stage,
    message: event.message,
  }))
}

function mergeEvents(existing: Array<any>, incoming: Array<any>) {
  const byId = new Map(existing.map((event) => [event.id, event]))
  for (const event of incoming) byId.set(event.id, event)
  return Array.from(byId.values()).sort((left, right) => {
    const leftTime = new Date(left.createdAt ?? 0).getTime()
    const rightTime = new Date(right.createdAt ?? 0).getTime()
    if (leftTime !== rightTime) return leftTime - rightTime
    return String(left.id).localeCompare(String(right.id))
  })
}

export function mergePlanCheckStreamMessage(detail: any, message: any) {
  if (message.type === 'snapshot') {
    return {
      ...detail,
      run: {
        ...detail.run,
        status: message.run.status,
        failureReason: message.run.failureReason ?? null,
        startedAt: message.run.startedAt ?? detail.run.startedAt,
        completedAt: message.run.completedAt ?? detail.run.completedAt,
        updatedAt: message.run.updatedAt ?? detail.run.updatedAt,
        coverageStats: message.run.coverageStats ?? detail.run.coverageStats,
        sourcePdfUrl:
          message.run.artifactUrls?.source ?? detail.run.sourcePdfUrl,
        reportPdfUrl:
          message.run.artifactUrls?.report ?? detail.run.reportPdfUrl,
        annotatedPdfUrl:
          message.run.artifactUrls?.annotated ?? detail.run.annotatedPdfUrl,
      },
      auditEvents: mergeEvents(detail.auditEvents ?? [], message.events ?? []),
      liveProgress: message.progress,
    }
  }
  if (message.type === 'heartbeat') {
    const serverTime = new Date(message.serverTime).getTime()
    const startedAt = detail.run.startedAt
      ? new Date(detail.run.startedAt).getTime()
      : null
    const updatedAt = detail.run.updatedAt
      ? new Date(detail.run.updatedAt).getTime()
      : null
    return {
      ...detail,
      liveProgress: detail.liveProgress
        ? {
            ...detail.liveProgress,
            elapsedSeconds:
              startedAt && Number.isFinite(serverTime)
                ? Math.max(0, Math.floor((serverTime - startedAt) / 1000))
                : detail.liveProgress.elapsedSeconds,
            lastUpdateSecondsAgo:
              updatedAt && Number.isFinite(serverTime)
                ? Math.max(0, Math.floor((serverTime - updatedAt) / 1000))
                : detail.liveProgress.lastUpdateSecondsAgo,
          }
        : detail.liveProgress,
    }
  }
  if (message.type === 'event') {
    return {
      ...detail,
      auditEvents: mergeEvents(detail.auditEvents ?? [], [message.event]),
    }
  }
  if (message.type === 'terminal') {
    return {
      ...detail,
      run: {
        ...detail.run,
        status: message.status,
        failureReason: message.failureReason ?? detail.run.failureReason,
        startedAt: message.run?.startedAt ?? detail.run.startedAt,
        completedAt: message.run?.completedAt ?? detail.run.completedAt,
        updatedAt: message.run?.updatedAt ?? detail.run.updatedAt,
        sourcePdfUrl:
          message.run?.artifactUrls?.source ?? detail.run.sourcePdfUrl,
        reportPdfUrl:
          message.run?.artifactUrls?.report ?? detail.run.reportPdfUrl,
        annotatedPdfUrl:
          message.run?.artifactUrls?.annotated ?? detail.run.annotatedPdfUrl,
      },
      liveProgress: message.progress ?? detail.liveProgress,
    }
  }
  return detail
}

export function canRetryPlanCheckRun(detail: any) {
  if (detail.run.status === 'failed') return true
  return (
    detail.run.status === 'processing' &&
    typeof detail.liveProgress?.lastUpdateSecondsAgo === 'number' &&
    detail.liveProgress.lastUpdateSecondsAgo >= PLAN_CHECK_STALE_RETRY_SECONDS
  )
}

export function usePlanCheckRunStream(
  runId: string,
  initialDetail: any,
  options: {
    onRefresh?: () => void
    refreshDelayMs?: number
  } = {},
) {
  const onRefresh = options.onRefresh
  const refreshDelayMs = options.refreshDelayMs
  const initialVersion = [
    runId,
    String(initialDetail.run?.status ?? ''),
    String(initialDetail.run?.updatedAt ?? ''),
    String(initialDetail.run?.reportPdfUrl ?? ''),
    String(initialDetail.run?.annotatedPdfUrl ?? ''),
    String(initialDetail.auditEvents?.length ?? 0),
    String(initialDetail.assessments?.length ?? 0),
    String(initialDetail.pages?.length ?? 0),
    String(initialDetail.annotations?.length ?? 0),
  ].join(':')
  const [detail, setDetail] = useState(initialDetail)
  const [streamState, setStreamState] = useState<
    'connecting' | 'open' | 'reconnecting' | 'closed'
  >('connecting')

  useEffect(() => {
    setDetail((current: any) => ({
      ...initialDetail,
      liveProgress: current.liveProgress,
    }))
  }, [initialVersion])

  useEffect(() => {
    if (typeof EventSource === 'undefined') {
      setStreamState('closed')
      return
    }
    const source = new EventSource(
      `/api/plan-checks/${encodeURIComponent(runId)}/stream`,
    )
    let invalidateTimer: ReturnType<typeof setTimeout> | null = null
    const scheduleRefresh = () => {
      if (!onRefresh || invalidateTimer) return
      invalidateTimer = setTimeout(() => {
        invalidateTimer = null
        onRefresh()
      }, refreshDelayMs ?? 500)
    }
    const handleMessage = (event: MessageEvent) => {
      const message = JSON.parse(event.data)
      setDetail((current: any) => mergePlanCheckStreamMessage(current, message))
      if (message.type === 'event' || message.type === 'terminal') {
        scheduleRefresh()
      }
    }
    source.addEventListener('open', () => setStreamState('open'))
    source.addEventListener('snapshot', handleMessage)
    source.addEventListener('event', handleMessage)
    source.addEventListener('heartbeat', handleMessage)
    source.addEventListener('terminal', (event) => {
      handleMessage(event)
      setStreamState('closed')
      source.close()
    })
    source.addEventListener('error', () => {
      setStreamState((current) =>
        current === 'closed' ? 'closed' : 'reconnecting',
      )
    })
    return () => {
      if (invalidateTimer) clearTimeout(invalidateTimer)
      source.close()
    }
  }, [runId, onRefresh, refreshDelayMs])

  return { detail, streamState }
}

function PlanCheckDetailPage() {
  const loaderDetail = Route.useLoaderData()
  const router = useRouter()
  const streamOptions = useMemo(
    () => ({
      onRefresh: () => {
        void router.invalidate()
      },
    }),
    [router],
  )
  const { detail, streamState } = usePlanCheckRunStream(
    loaderDetail.run.id,
    loaderDetail,
    streamOptions,
  )
  const [resultFilter, setResultFilter] = useState('')
  const [partFilter, setPartFilter] = useState('')
  const [queryFilter, setQueryFilter] = useState('')
  const stats = detail.run.coverageStats || {}
  const liveProgress = detail.liveProgress ?? null
  const availableParts = useMemo(
    () =>
      Array.from(
        new Set(
          detail.assessments
            .map((assessment) => assessment.part || '')
            .filter(Boolean),
        ),
      ).sort(),
    [detail.assessments],
  )
  const filteredAssessments = useMemo(
    () =>
      filterPlanCheckAssessments(detail.assessments, {
        result: resultFilter,
        part: partFilter,
        query: queryFilter,
      }),
    [detail.assessments, partFilter, queryFilter, resultFilter],
  )
  const grouped = groupPlanCheckAssessments(detail.assessments)
  const timeline = buildPlanCheckTimeline(detail.auditEvents)
  const statusLabel = detail.run.status.replace(/_/g, ' ')
  const artifactCount = [
    detail.run.sourcePdfUrl,
    detail.run.reportPdfUrl,
    detail.run.annotatedPdfUrl,
  ].filter(Boolean).length
  const canRetry = canRetryPlanCheckRun(detail)

  const handleRetry = async () => {
    trackAnalyticsEvent('plan_check_retry_clicked', {
      run_id: detail.run.id,
      status: detail.run.status,
    })
    await retryPlanCheck({ data: { runId: detail.run.id } })
    router.invalidate()
  }

  const handleRestart = async () => {
    trackAnalyticsEvent('plan_check_restart_clicked', {
      run_id: detail.run.id,
      status: detail.run.status,
    })
    if (
      !window.confirm(
        'Restart this plan check from scratch? Existing extracted pages, evidence, assessments and report artifacts will be replaced.',
      )
    ) {
      return
    }
    await restartPlanCheck({ data: { runId: detail.run.id } })
    trackAnalyticsEvent('plan_check_restart_confirmed', {
      run_id: detail.run.id,
    })
    router.invalidate()
  }

  return (
    <main className="mx-auto grid w-full max-w-7xl gap-6 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/plan-checks"
            onClick={() =>
              trackAnalyticsEvent('navigation_clicked', {
                destination: '/plan-checks',
                label: 'back_to_plan_checks',
                location: 'plan_check_detail',
              })
            }
            className="text-sm text-neutral-500 hover:text-neutral-900"
          >
            Back to plan checks
          </Link>
          <h1 className="mt-2 text-2xl font-semibold">{detail.run.title}</h1>
          <p className="text-sm text-neutral-500">
            {detail.run.sourceFilename}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span
              className={`rounded border px-2 py-1 font-medium ${statusTone(detail.run.status)}`}
            >
              {statusLabel}
            </span>
            <span className="text-neutral-600">
              {liveProgress?.currentStepLabel ?? 'Waiting for progress'}
            </span>
            {liveProgress ? (
              <>
                <span className="text-neutral-500">
                  Elapsed {formatSeconds(liveProgress.elapsedSeconds)}
                </span>
                <span className="text-neutral-500">
                  Updated {formatSeconds(liveProgress.lastUpdateSecondsAgo)} ago
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {detail.run.reportPdfUrl ? (
            <a
              href={detail.run.reportPdfUrl}
              onClick={() =>
                trackAnalyticsEvent('plan_check_artifact_opened', {
                  run_id: detail.run.id,
                  artifact_type: 'report_pdf',
                })
              }
            >
              <Button type="button">
                <Download className="h-4 w-4" />
                Report PDF
              </Button>
            </a>
          ) : null}
          {detail.run.annotatedPdfUrl ? (
            <a
              href={detail.run.annotatedPdfUrl}
              onClick={() =>
                trackAnalyticsEvent('plan_check_artifact_opened', {
                  run_id: detail.run.id,
                  artifact_type: 'annotated_pdf',
                })
              }
            >
              <Button type="button">
                <Download className="h-4 w-4" />
                Annotated PDF
              </Button>
            </a>
          ) : null}
          <Link
            to="/chat"
            onClick={() =>
              trackAnalyticsEvent('plan_check_opened_in_chat', {
                run_id: detail.run.id,
                status: detail.run.status,
              })
            }
            search={
              {
                planCheckRunId: detail.run.id,
                planCheckTitle: detail.run.title,
              } as any
            }
          >
            <Button type="button">
              <MessageSquare className="h-4 w-4" />
              Open in chat
            </Button>
          </Link>
          {canRetry ? (
            <>
              <Button type="button" onPress={handleRetry}>
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
              <Button type="button" onPress={handleRestart}>
                <RefreshCw className="h-4 w-4" />
                Restart
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        {[
          ['Checks', statValue(stats.totalChecks)],
          ['Issues', statValue(stats.issues)],
          ['Uncertain', statValue(stats.uncertain)],
          ['Not assessable', statValue(stats.notAssessable)],
          ['Passes', statValue(stats.passes)],
        ].map(([label, value]) => (
          <Card key={label} styles={fullWidthStyles}>
            <CardContent>
              <div className="text-2xl font-semibold">{value}</div>
              <div className="text-sm text-neutral-500">{label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {liveProgress || timeline.length ? (
        <Card styles={fullWidthStyles}>
          <CardHeader>
            <CardTitle>Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div aria-live="polite" className="text-sm text-neutral-600">
              {liveProgress
                ? `${liveProgress.currentStepLabel}: ${liveProgress.currentStepState}. Last update ${liveProgress.lastUpdateSecondsAgo}s ago.`
                : `The plan check is ${detail.run.status.replace(/_/g, ' ')}.`}{' '}
              Live updates are{' '}
              {streamState === 'open' ? 'connected' : streamState}.
            </div>
            {liveProgress ? (
              <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3 text-sm">
                <div className="min-w-0 rounded border border-neutral-200 bg-neutral-50 px-3 py-2">
                  Pages: {liveProgress.counts.pages}
                </div>
                <div className="min-w-0 rounded border border-neutral-200 bg-neutral-50 px-3 py-2">
                  Evidence: {liveProgress.counts.evidence}
                </div>
                <div className="min-w-0 rounded border border-neutral-200 bg-neutral-50 px-3 py-2">
                  Assessments: {liveProgress.counts.assessments}
                </div>
                <div className="min-w-0 rounded border border-neutral-200 bg-neutral-50 px-3 py-2">
                  Annotations: {liveProgress.counts.annotations}
                </div>
                <div className="min-w-0 rounded border border-neutral-200 bg-neutral-50 px-3 py-2">
                  Artifacts: {artifactCount}/3
                </div>
              </div>
            ) : null}
            <ol className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3 text-sm">
              {liveProgress
                ? liveProgress.steps.map((step: any) => (
                    <li
                      key={step.key}
                      className={`min-w-0 rounded border-l-4 bg-neutral-50 p-3 ${stepTone(step.state)}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-medium text-neutral-900">
                          {step.label}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {step.completedAt
                            ? new Date(step.completedAt).toLocaleTimeString()
                            : step.state.replace(/_/g, ' ')}
                        </div>
                      </div>
                      <div className="mt-1 break-words text-neutral-600">
                        {step.message || step.state.replace(/_/g, ' ')}
                      </div>
                    </li>
                  ))
                : timeline.map((event) => (
                    <li
                      key={event.id}
                      className="min-w-0 rounded border-l-4 border-neutral-300 bg-neutral-50 p-3"
                    >
                      <div className="font-medium text-neutral-900">
                        {event.stage.replace(/_/g, ' ')}
                      </div>
                      <div className="mt-1 break-words text-neutral-600">
                        {event.message}
                      </div>
                    </li>
                  ))}
            </ol>
          </CardContent>
        </Card>
      ) : null}

      {detail.run.failureReason ? (
        <Card styles={fullWidthStyles}>
          <CardContent>
            <div className="text-sm text-red-700">
              {detail.run.failureReason}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card styles={fullWidthStyles}>
          <CardHeader>
            <CardTitle>Compliance Coverage Matrix</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 grid gap-3 md:grid-cols-3">
              <select
                className="rounded border border-neutral-300 bg-white px-3 py-2 text-sm"
                value={resultFilter}
                onChange={(event) => {
                  setResultFilter(event.target.value)
                  trackAnalyticsEvent('plan_check_filter_changed', {
                    filter: 'result',
                    has_value: Boolean(event.target.value),
                  })
                }}
                aria-label="Filter by result"
              >
                <option value="">All results</option>
                <option value="issue">Issues</option>
                <option value="uncertain_needs_verification">Uncertain</option>
                <option value="not_assessable">Not assessable</option>
                <option value="not_applicable">Not applicable</option>
                <option value="pass">Passes</option>
              </select>
              <select
                className="rounded border border-neutral-300 bg-white px-3 py-2 text-sm"
                value={partFilter}
                onChange={(event) => {
                  setPartFilter(event.target.value)
                  trackAnalyticsEvent('plan_check_filter_changed', {
                    filter: 'part',
                    has_value: Boolean(event.target.value),
                  })
                }}
                aria-label="Filter by part"
              >
                <option value="">All parts</option>
                {availableParts.map((part) => (
                  <option key={part} value={part}>
                    {part}
                  </option>
                ))}
              </select>
              <input
                className="rounded border border-neutral-300 px-3 py-2 text-sm"
                value={queryFilter}
                onChange={(event) => setQueryFilter(event.target.value)}
                onBlur={() => {
                  if (!queryFilter.trim()) return
                  trackAnalyticsEvent('plan_check_matrix_searched', {
                    query_length: queryFilter.trim().length,
                  })
                }}
                placeholder="Search matrix"
                aria-label="Search matrix"
              />
            </div>
            <div className="overflow-x-auto">
              {detail.assessments.length === 0 ? (
                <p className="rounded border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
                  Assessment has not started yet.
                </p>
              ) : filteredAssessments.length === 0 ? (
                <p className="rounded border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
                  No matching assessment rows.
                </p>
              ) : (
                <table className="w-full min-w-[860px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b text-left text-neutral-500">
                      <th className="py-2 pr-3">Result</th>
                      <th className="py-2 pr-3">Part</th>
                      <th className="py-2 pr-3">Check</th>
                      <th className="py-2 pr-3">Confidence</th>
                      <th className="py-2 pr-3">Explanation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssessments.map((assessment) => (
                      <tr key={assessment.id} className="border-b align-top">
                        <td className="py-3 pr-3">
                          <span
                            className={`rounded border px-2 py-1 text-xs font-medium ${resultTone(assessment.result)}`}
                          >
                            {assessment.result.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="py-3 pr-3">
                          {assessment.part ||
                            assessment.jurisdiction ||
                            'Corpus'}
                        </td>
                        <td className="py-3 pr-3 font-medium">
                          {assessment.title}
                        </td>
                        <td className="py-3 pr-3">
                          {Math.round((assessment.confidenceBps ?? 0) / 100)}%
                        </td>
                        <td className="py-3 pr-3 text-neutral-700">
                          {assessment.explanation}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
        </Card>

        <aside className="min-w-0 space-y-4">
          {[
            ['Issues', grouped.issues],
            ['Uncertain', grouped.uncertain],
            ['Not assessable', grouped.notAssessable],
          ].map(([label, items]) => (
            <Card key={label as string} styles={fullWidthStyles}>
              <CardHeader>
                <CardTitle>{label as string}</CardTitle>
              </CardHeader>
              <CardContent>
                {(items as typeof detail.assessments).length ? (
                  <ul className="space-y-2 text-sm">
                    {(items as typeof detail.assessments)
                      .slice(0, 8)
                      .map((item) => (
                        <li
                          key={item.id}
                          className="rounded border border-neutral-200 p-2"
                        >
                          <div className="font-medium">{item.title}</div>
                          <div className="text-neutral-600">
                            {item.explanation}
                          </div>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="text-sm text-neutral-500">No records.</p>
                )}
              </CardContent>
            </Card>
          ))}

          <Card styles={fullWidthStyles}>
            <CardHeader>
              <CardTitle>Assumptions</CardTitle>
            </CardHeader>
            <CardContent>
              {detail.run.inferredAssumptions.length ? (
                <ul className="space-y-2 text-sm text-neutral-700">
                  {detail.run.inferredAssumptions.map(
                    (item: string, index: number) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ),
                  )}
                </ul>
              ) : (
                <p className="text-sm text-neutral-500">
                  No assumptions recorded yet.
                </p>
              )}
            </CardContent>
          </Card>

          <Card styles={fullWidthStyles}>
            <CardHeader>
              <CardTitle>Pages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {detail.pages.map((page) => (
                  <a
                    key={page.id}
                    href={
                      page.previewImageUrl || page.renderedImageUrl || undefined
                    }
                    onClick={() =>
                      trackAnalyticsEvent('plan_check_page_preview_opened', {
                        run_id: detail.run.id,
                        has_annotations: detail.annotations.some(
                          (annotation) =>
                            annotation.pageNumber === page.pageNumber,
                        ),
                      })
                    }
                    className="block rounded border border-neutral-200 p-2 text-sm"
                  >
                    <div className="relative overflow-hidden rounded bg-neutral-50">
                      {page.previewImageUrl ? (
                        <img
                          src={page.previewImageUrl}
                          alt={`Page ${page.pageNumber} preview`}
                          className="block w-full"
                        />
                      ) : (
                        <div className="aspect-[4/3] p-3">
                          Page {page.pageNumber}
                        </div>
                      )}
                      {detail.annotations
                        .filter(
                          (annotation) =>
                            annotation.pageNumber === page.pageNumber,
                        )
                        .map((annotation) => (
                          <span
                            key={annotation.id}
                            className="absolute border-2 border-red-600 bg-red-500/10"
                            style={{
                              left: `${annotation.boundingBox.left * 100}%`,
                              top: `${annotation.boundingBox.top * 100}%`,
                              width: `${annotation.boundingBox.width * 100}%`,
                              height: `${annotation.boundingBox.height * 100}%`,
                            }}
                            title={annotation.label}
                          />
                        ))}
                    </div>
                    <div className="mt-2">Page {page.pageNumber}</div>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </main>
  )
}

export const __planCheckDetailTestUtils = {
  formatSeconds,
  resultTone,
  statValue,
  statusTone,
  stepTone,
}
